import React, { useEffect, useState } from 'react';
import {
  Text,
  Button,
  Input,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Select,
  Spinner,
} from '@fluentui/react-components';
import { useTenant } from '../../context/TenantContext';
import './TeamMembers.css';

export const TeamMembers: React.FC = () => {
  const { users, loading, error, fetchUsers, addUser, removeUser, updateUserRole } = useTenant();
  const [isInviting, setIsInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'member' as const,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleInviteChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.currentTarget;
    setInviteForm(prev => ({
      ...prev,
      [name]: value as any,
    }));
  };

  const handleInviteSubmit = async () => {
    setIsInviting(true);
    try {
      await addUser(inviteForm.email, inviteForm.name, inviteForm.role);
      setInviteForm({ email: '', name: '', role: 'member' });
      alert('User invited successfully');
    } catch (err) {
      alert(`Failed to invite user: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (confirm('Are you sure you want to remove this user?')) {
      try {
        await removeUser(userId);
      } catch (err) {
        alert(`Failed to remove user: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }
  };

  const handleChangeRole = async (userId: string, newRole: string) => {
    try {
      await updateUserRole(userId, newRole as any);
    } catch (err) {
      alert(`Failed to update role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  if (loading) {
    return <Spinner label="Loading team members..." />;
  }

  return (
    <div className="team-members-container">
      <div className="team-members-header">
        <Text as="h2" size={400} weight="semibold">
          Team Members
        </Text>
        <Dialog>
          <DialogTrigger disableButtonEnhancement>
            <Button appearance="primary">+ Invite Member</Button>
          </DialogTrigger>
          <DialogSurface>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogBody>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                <Text size={200} weight="semibold">Email Address</Text>
                <Input
                  name="email"
                  value={inviteForm.email}
                  onChange={handleInviteChange}
                  placeholder="colleague@company.com"
                  disabled={isInviting}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                <Text size={200} weight="semibold">Full Name</Text>
                <Input
                  name="name"
                  value={inviteForm.name}
                  onChange={handleInviteChange}
                  placeholder="John Doe"
                  disabled={isInviting}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                <Text size={200} weight="semibold">Role</Text>
                <Select
                  name="role"
                  value={inviteForm.role}
                  onChange={handleInviteChange}
                  disabled={isInviting}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </Select>
              </label>
            </DialogBody>
            <DialogActions>
              <Button appearance="secondary">Cancel</Button>
              <Button 
                appearance="primary" 
                onClick={handleInviteSubmit}
                disabled={isInviting || !inviteForm.email || !inviteForm.name}
              >
                {isInviting ? <Spinner size="tiny" /> : 'Send Invite'}
              </Button>
            </DialogActions>
          </DialogSurface>
        </Dialog>
      </div>

      {error && (
        <div className="error-message">
          <Text>{error}</Text>
        </div>
      )}

      <div className="team-members-list">
        {users.length === 0 ? (
          <Text>No team members yet. Invite someone to get started!</Text>
        ) : (
          <table className="members-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user: any) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <Select
                      value={user.role}
                      onChange={(e) => handleChangeRole(user.id, e.currentTarget.value)}
                      disabled={user.status === 'pending'}
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                      <option value="owner">Owner</option>
                    </Select>
                  </td>
                  <td>
                    <span className={`status ${user.status === 'pending' ? 'pending' : (user.isActive ? 'active' : 'inactive')}`}>
                      {user.status === 'pending' ? 'Invited' : (user.isActive ? 'Active' : 'Inactive')}
                    </span>
                  </td>
                  <td>
                    <Button
                      appearance="subtle"
                      onClick={() => handleRemoveUser(user.id)}
                      title={user.status === 'pending' ? 'Cancel invite' : 'Remove user'}
                    >
                      {user.status === 'pending' ? 'Cancel' : 'Remove'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TeamMembers;
